// Loads the Vosk WASM runtime from a hosted, versioned asset URL rather than the
// app bundle, matching how ONNX Runtime is loaded (engine/asr.js loadOrtModule).
// vosk-browser ships as a UMD bundle that assigns a global `Vosk`; this injects
// it once and resolves that global. Memoized per URL.

/** @type {Map<string, Promise<object>>} */
const runtimeByUrl = new Map();

/**
 * @returns {object|null} The global Vosk runtime if already loaded.
 */
function existingGlobalVosk() {
  return (typeof globalThis !== 'undefined' && globalThis.Vosk) ? globalThis.Vosk : null;
}

/**
 * Loads and resolves the Vosk runtime (with `createModel`). Rejects when there is
 * no DOM and no global `Vosk` — callers without a DOM must inject the runtime via
 * `createVoskAsr({ voskModule })`.
 * @param {string} runtimeUrl
 * @returns {Promise<object>}
 */
export function loadVoskRuntime(runtimeUrl) {
  const preloaded = existingGlobalVosk();
  if (preloaded) {
    return Promise.resolve(preloaded);
  }

  if (typeof runtimeUrl !== 'string' || runtimeUrl.length === 0) {
    return Promise.reject(new TypeError('loadVoskRuntime: runtimeUrl must be a non-empty string'));
  }

  const inFlight = runtimeByUrl.get(runtimeUrl);
  if (inFlight) {
    return inFlight;
  }

  const promise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined' || !document.head) {
      reject(new Error('loadVoskRuntime: no DOM available; inject voskModule explicitly'));
      return;
    }
    const script = document.createElement('script');
    script.src = runtimeUrl;
    script.async = true;
    script.onload = () => {
      const vosk = existingGlobalVosk();
      if (vosk) {
        resolve(vosk);
      } else {
        reject(new Error(`loadVoskRuntime: ${runtimeUrl} loaded but global Vosk is undefined`));
      }
    };
    script.onerror = () => reject(new Error(`loadVoskRuntime: failed to load ${runtimeUrl}`));
    document.head.appendChild(script);
  });

  // Drop the memo on failure so a retry can re-inject.
  promise.catch(() => runtimeByUrl.delete(runtimeUrl));
  runtimeByUrl.set(runtimeUrl, promise);
  return promise;
}
